import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link2, Search, Loader2, ExternalLink, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { getAvailableOpportunities, updateClaim } from '../../api/claims';

const LinkOpportunityModal = ({ open, onOpenChange, claimId, currentOpportunityId, onSuccess }) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOpportunityId, setSelectedOpportunityId] = useState(currentOpportunityId);

  // Fetch available pipeline pursuits
  const { data: opportunitiesData, isLoading } = useQuery({
    queryKey: ['availableOpportunities', searchTerm],
    queryFn: () => getAvailableOpportunities(searchTerm),
    enabled: open
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (opportunityId) => updateClaim(claimId, { opportunity_id: opportunityId }),
    onSuccess: () => {
      queryClient.invalidateQueries(['claimDetail', claimId]);
      queryClient.invalidateQueries(['claims']);
      if (onSuccess) onSuccess();
      onOpenChange(false);
    }
  });

  const opportunities = opportunitiesData?.data || [];

  const handleLink = () => {
    if (selectedOpportunityId) {
      updateMutation.mutate(selectedOpportunityId);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Link to Pursuit
          </DialogTitle>
          <DialogDescription>
            Link this claim to a pipeline pursuit to automatically populate volume, pricing, and part data.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Search */}
          <div className="space-y-2">
            <Label htmlFor="search">Search Pursuits</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="search"
                type="text"
                placeholder="Search by pursuit name or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Pursuits List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-600">Loading pursuits...</span>
            </div>
          ) : opportunities.length === 0 ? (
            <Alert>
              <AlertDescription>
                No pursuits found. {searchTerm && 'Try a different search term.'}
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {opportunities.map((opportunity) => (
                <div
                  key={opportunity.id}
                  onClick={() => setSelectedOpportunityId(opportunity.id)}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    selectedOpportunityId === opportunity.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900">{opportunity.opportunity_name}</h4>
                        {selectedOpportunityId === opportunity.id && (
                          <CheckCircle2 className="h-4 w-4 text-blue-600" />
                        )}
                        {opportunity.id === currentOpportunityId && (
                          <Badge variant="secondary" className="text-xs">
                            Current
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <span>{opportunity.customer_name}</span>
                        {opportunity.part_number && (
                          <>
                            <span>•</span>
                            <span>Part: {opportunity.part_number}</span>
                          </>
                        )}
                        <span>•</span>
                        <Badge variant="outline" className="text-xs">
                          {opportunity.stage}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {updateMutation.isError && (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to link pursuit. Please try again.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleLink}
            disabled={!selectedOpportunityId || selectedOpportunityId === currentOpportunityId || updateMutation.isPending}
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Linking...
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4 mr-2" />
                Link Pursuit
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkOpportunityModal;
